/// <amd-module name="Wit/DeliveryPlans/DependencyPanel/DependencyPanel" />
import * as React from "react";
import { ObservableValue, ReadyableObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { useComponentContext } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { ContentSize } from "VSSUI/Callout";
import { IListItemDetails, ListItem, ScrollableList } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ZeroData } from "VSSUI/ZeroData";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { TableStatus } from "Wit/Common/WitSettingsViews/Components/TableStatus";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import * as Resources from "Wit/DeliveryPlans/Resources";
import { DependencyErrorType, WorkItemDependencyPanelData } from "../Common/Client/Contracts/DataProvider";
import { DependencyRow, WorkItemDependency } from "../Common/Components/WorkItemDependency";
import { IDeliveryPlansWorkItem } from "../Common/Interfaces";
import { IDeliveryPlansIterationService } from "../Common/Services/DeliveryPlansIterationService";
import { IDependencyService } from "./DependencyService";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";

export interface IDependencyPanel {
    onDismiss: () => void;
    workItem: IDeliveryPlansWorkItem;
}

const zeroDataAssetId = "ms.vss-work-web/delivery-plans-content/Content/plans-landing.svg";

export const DependencyPanel: React.FunctionComponent<IDependencyPanel> = ({ onDismiss, workItem }) => {
    const { pageContext } = useComponentContext();

    const dependencyService = pageContext.getService<IDependencyService>("IDependencyService");
    const dependencyDataPromise = dependencyService.getDependencies(workItem.id);
    const dependencyData = new ReadyableObservableArray<DependencyRow>([]);
    const errorMessage = new ObservableValue<string | undefined>(undefined);
    let fieldsPromise : Promise<IFieldEntry[] | undefined>;
    let fieldService : IWorkItemFieldService | IWorkItemFieldService2;
    if (isFeatureFlagEnabled(pageContext, FeatureFlags.UseNewWorkItemFieldService, false)) {
        fieldService = pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2");
        fieldsPromise = fieldService.loadFields();
    } else {
        fieldService = pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
        fieldsPromise = fieldService.getFieldsAsync();
    }
    const loading = new ObservableValue(true);
    const loadDependencies = (dependencies: WorkItemDependencyPanelData) => {
        if (dependencies.dependencyData) {
            dependencyData.value = dependencies.dependencyData
                .map(d => ({ ...d, expanded: new ObservableValue(d.dependencyError !== DependencyErrorType.None) }))
                // sort dependencies with source work item type to the top
                .sort((a, b) => {
                    // since source dependency has to be related to the item we for which we loaded dependencies,
                    // chain length will always indicate hierarchy level
                    return a.sourceChain.length - b.sourceChain.length;
                })
                // sort bad dependencies to the top
                .sort((a, b) => {
                    if (a.dependencyError === b.dependencyError) {
                        return 0;
                    }

                    const aHasError = a.dependencyError !== DependencyErrorType.None;
                    const bHasError = b.dependencyError !== DependencyErrorType.None;
                    if (aHasError === bHasError) {
                        return 0;
                    }

                    return aHasError ? -1 : 1;
                });
        }

        if (dependencies.iterationsList) {
            const iterationService = pageContext.getService<IDeliveryPlansIterationService>("IDeliveryPlansIterationService");
            dependencies.iterationsList.forEach(i => iterationService.addIteration({ ...i, id: i.iterationId }));
        }

        dependencyData.ready.value = true;
    };

    const onSaveWorkItem = async () => {
        // if a work item gets saved from the form, we have to reload dependencies
        // to ensure dependency data is up to date
        dependencyData.ready.value = false;
        const dependencies = await dependencyService.getDependencies(workItem.id, /** ignoreCache*/ true);
        if (dependencies) {
            loadDependencies(dependencies);
        } else {
            errorMessage.value = dependencyService.getDependenciesErrorMessage();
        }
    };

    const renderDependencyRow = (rowIndex: number, item: DependencyRow, details: IListItemDetails<DependencyRow>) => {
        return (
            <ListItem className="padding-horizontal-16" index={rowIndex} details={details}>
                <WorkItemDependency {...item} onSaveWorkItem={onSaveWorkItem} rowIndex={rowIndex} />
            </ListItem>
        );
    };

    React.useEffect(() => {
        Promise.all([dependencyDataPromise, fieldsPromise]).then(([dependencies, fields]) => {
            loading.value = false;
            if (dependencies && fields) {
                loadDependencies(dependencies);
            } else if (!dependencies) {
                errorMessage.value = dependencyService.getDependenciesErrorMessage();
            } else if (!fields) {
                errorMessage.value = fieldService.getFieldsErrorMessage();
            }
        });
    }, []);

    const title = workItem.getFieldValue<string>(CoreFieldRefNames.Title);
    return (
        <Panel
            className="delivery-plans-dependency-panel"
            contentClassName="custom-scrollbar justify-center"
            description={format(Resources.DependencyPanelDescription, workItem.id, title)}
            footerButtonProps={[{ text: Resources.CloseButtonText, onClick: onDismiss }]}
            lightDismiss
            onDismiss={onDismiss}
            portalProps={{ parentClassName: "dependency-panel-portal" }}
            size={ContentSize.ExtraLarge}
            titleProps={{ text: Resources.DependencyPanelHeader }}
        >
            <TableStatus dataProviderErrorMessage={errorMessage} />
            <Observer ready={dependencyData.ready} errorMessage={errorMessage}>
                {({ ready, errorMessage }: { ready: boolean; errorMessage: string | undefined }) => {
                    if (ready) {
                        return dependencyData.value.length > 0 ? (
                            <ScrollableList
                                itemProvider={dependencyData}
                                onActivate={(e, listRow) => {
                                    if (listRow.data.expanded) {
                                        listRow.data.expanded.value = !listRow.data.expanded.value;
                                    }
                                }}
                                renderRow={renderDependencyRow}
                                singleClickActivation
                                width="100%"
                            />
                        ) : (
                            <div className="flex-column flex-grow flex-center justify-center margin-bottom-16">
                                <ZeroData
                                    imagePath={getAssetLocation(pageContext, zeroDataAssetId)}
                                    imageAltText={""}
                                    primaryText={Resources.NoDependencies}
                                />
                            </div>
                        );
                    } else if (!errorMessage) {
                        return <Spinner className="justify-center" size={SpinnerSize.large} label={Resources.LoadingDependencies} />;
                    } else {
                        return null;
                    }
                }}
            </Observer>
        </Panel>
    );
};
